import { ExpandLess, ExpandMore } from "@mui/icons-material";
import type React from "react";
import { useState } from "react";
import styles from "../styles.module.scss";

interface ToolCallProgressProps {
  toolCall: {
    toolName: string;
    toolCallId: string;
    args?: Record<string, unknown>;
    state?: "running" | "success" | "error";
    result?: Record<string, unknown>;
    error?: string;
    progressSteps?: Array<{
      stepId?: string;
      stepName: string;
      status: "running" | "success" | "error";
      startedAt?: string;
      finishedAt?: string;
    }>;
    displayName?: string;
    isThinking?: boolean;
  };
}

const ToolCallProgress: React.FC<ToolCallProgressProps> = ({ toolCall }) => {
  // updateWorkingMemoryツールは表示しない（Hookを条件分岐の前に呼ぶためearly returnは後で実行）
  const shouldHide = toolCall.toolName === "updateWorkingMemory";

  const state = toolCall.state || "running";
  // ステータスはアイコンのみで表現するため、テキストは表示しない

  // ツールチップ内容（ステップの積み上げ表示）
  const steps = toolCall.progressSteps || toolCall.result?.["steps"] || [];
  const hasSteps = Array.isArray(steps) && steps.length > 0;
  const [expanded, setExpanded] = useState(false);

  if (shouldHide) {
    return null;
  }

  return (
    <div className={styles["toolChip"]} data-state={state}>
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <div className={styles["toolChipIcon"]} aria-hidden style={{ marginRight: 8 }}>
          {state === "running" && <span className={styles["spinner"]} />}
          {state === "success" && <span>✓</span>}
          {state === "error" && <span>!</span>}
          {state === "running" || state === "success" || state === "error" ? null : <span>…</span>}
        </div>
        <div className={styles["toolChipLabel"]} style={{ flex: 1 }}>
          {toolCall.isThinking && toolCall.state === "running" ? (
            <>
              REQXIGNが考え中
              <span className={styles["loadingDots"]}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </>
          ) : (
            toolCall.displayName
          )}
        </div>
        {hasSteps && (
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "折りたたむ" : "展開する"}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: 4,
            }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </button>
        )}
      </div>

      {expanded && hasSteps && (
        <div style={{ marginTop: 6 }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {steps.map((s: { stepId?: string; stepName: string; status?: string }, idx: number) => {
              const status = (s.status as "running" | "success" | "error") || "running";
              const dotColor =
                status === "success" ? "#16a34a" : status === "error" ? "#dc2626" : "#2563eb";
              const statusLabel =
                status === "success" ? "完了" : status === "error" ? "失敗" : "実行中";
              return (
                <li
                  key={s.stepId || `${s.stepName}-${idx}`}
                  style={{ display: "flex", alignItems: "center", padding: "2px 0" }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      background: dotColor,
                      marginRight: 8,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 12 }}>{s.stepName || "Step"}</span>
                  <span style={{ fontSize: 12, color: "#64748b" }}>{statusLabel}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ToolCallProgress;
